import { useRef, useState, useEffect, useCallback } from "react";

const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL;
const MAX_RECONNECT_DELAY = 30000;
const HEARTBEAT_INTERVAL = 25000;
const POLL_INTERVAL = 100;

// Mensaje de keep-alive: mantiene viva la conexión frente a los timeouts de
// idle de proxies/balanceadores y permite detectar sockets half-open.
// Los receptores lo descartan antes de llegar al onMessage del consumidor.
export const PING_MESSAGE = "__ping__";

// Extrae el payload de un mensaje del hub (viene como string JSON o como objeto).
export const parseWsPayload = (message) => {
  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }
  if (message && typeof message === "object") return message;
  return null;
};

const detachSocket = (socket) => {
  if (!socket) return;
  socket.onopen = null;
  socket.onmessage = null;
  socket.onerror = null;
  socket.onclose = null;
  if (socket.readyState < WebSocket.CLOSING) {
    socket.close(1000);
  }
};

export const useWebSocket = (hubId, groupId, connectionType, onMessage) => {
  const ws = useRef(null);
  const mountedRef = useRef(true);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const onMessageRef = useRef(onMessage);
  const connectRef = useRef(null);
  const [connectionState, setConnectionState] = useState(WebSocket.CLOSED);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimer.current) {
      clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatTimer.current = setInterval(() => {
      const socket = ws.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      try {
        socket.send(JSON.stringify({ Type: 3, Message: PING_MESSAGE }));
      } catch (e) {
        console.error("WebSocket heartbeat error:", e);
      }
    }, HEARTBEAT_INTERVAL);
  }, [stopHeartbeat]);

  const disconnect = useCallback(() => {
    clearReconnectTimer();
    stopHeartbeat();
    if (ws.current) {
      detachSocket(ws.current);
      ws.current = null;
    }
    setConnectionState(WebSocket.CLOSED);
  }, [clearReconnectTimer, stopHeartbeat]);

  const connect = useCallback(() => {
    clearReconnectTimer();
    stopHeartbeat();
    if (!groupId) return;

    detachSocket(ws.current);

    const clientId = crypto.randomUUID();
    const url = `wss://${WS_BASE_URL}/${hubId}?id=${clientId}&group=${groupId}&type=${connectionType}`;
    const socket = new WebSocket(url);
    ws.current = socket;
    setConnectionState(WebSocket.CONNECTING);

    socket.onopen = () => {
      if (ws.current !== socket) return;
      if (!mountedRef.current) {
        detachSocket(socket);
        return;
      }
      reconnectAttempts.current = 0;
      setConnectionState(WebSocket.OPEN);
      startHeartbeat();
    };

    socket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        console.error("WebSocket message parse error:", e);
        return;
      }
      if (data?.Message === PING_MESSAGE) return;
      onMessageRef.current(data);
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    socket.onclose = (event) => {
      if (ws.current !== socket) return;
      stopHeartbeat();
      setConnectionState(WebSocket.CLOSED);
      // Los cierres provocados por nosotros desenganchan este handler antes de
      // cerrar, así que cualquier onclose que llegue aquí es del servidor o de
      // la red: reintentamos siempre, tambien con code 1000 (idle timeout).
      if (!mountedRef.current) return;
      const attempt = reconnectAttempts.current;
      reconnectAttempts.current = attempt + 1;
      const delay =
        Math.min(MAX_RECONNECT_DELAY, 1000 * 2 ** attempt) + Math.random() * 1000;
      console.warn(
        `WebSocket closed (code ${event.code}); reconnecting in ${Math.round(delay)}ms`
      );
      reconnectTimer.current = setTimeout(() => connectRef.current?.(), delay);
    };
  }, [hubId, groupId, connectionType, clearReconnectTimer, startHeartbeat, stopHeartbeat]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  // Espera a que el socket esté OPEN, forzando una reconexión si hace falta.
  // Evita el caso "el usuario pulsa Enviar mientras seguimos en CONNECTING".
  const ensureConnected = useCallback(
    (timeout = 5000) =>
      new Promise((resolve) => {
        const socket = ws.current;
        if (socket?.readyState === WebSocket.OPEN) {
          resolve(true);
          return;
        }
        if (!socket || socket.readyState > WebSocket.OPEN) connect();

        const deadline = Date.now() + timeout;
        const interval = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            clearInterval(interval);
            resolve(true);
          } else if (Date.now() >= deadline) {
            clearInterval(interval);
            resolve(false);
          }
        }, POLL_INTERVAL);
      }),
    [connect]
  );

  // Espera a que el navegador haya volcado a la red todo lo encolado. Cerrar el
  // socket sin esto puede perder el payload en redes móviles lentas.
  const waitForFlush = useCallback(
    (timeout = 10000) =>
      new Promise((resolve) => {
        const socket = ws.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
          resolve(false);
          return;
        }
        if (socket.bufferedAmount === 0) {
          resolve(true);
          return;
        }

        const deadline = Date.now() + timeout;
        const interval = setInterval(() => {
          if (socket.readyState !== WebSocket.OPEN) {
            clearInterval(interval);
            resolve(false);
          } else if (socket.bufferedAmount === 0) {
            clearInterval(interval);
            resolve(true);
          } else if (Date.now() >= deadline) {
            clearInterval(interval);
            resolve(false);
          }
        }, POLL_INTERVAL);
      }),
    []
  );

  const sendMessage = useCallback((message, webSocketId) => {
    const socket = ws.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.warn("WebSocket not open; message not sent");
      return false;
    }
    const messageStr = typeof message === "string" ? message : JSON.stringify(message);
    const payload = { Type: 3, Message: messageStr };
    if (webSocketId) payload.webSocketId = webSocketId;
    try {
      socket.send(JSON.stringify(payload));
    } catch (e) {
      console.error("WebSocket send error:", e);
      return false;
    }
    console.log(`WS message sent (${messageStr.length} chars)`);
    return true;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, [disconnect]);

  useEffect(() => {
    if (!groupId) return;
    connect();
    return () => disconnect();
  }, [groupId, connect, disconnect]);

  // El móvil se va a background y el desktop se suspende: el socket muere sin
  // aviso. Al volver el foco o la red, reconectamos de inmediato en lugar de
  // esperar al backoff.
  useEffect(() => {
    if (!groupId) return;

    const reviveIfClosed = () => {
      const socket = ws.current;
      if (!socket || socket.readyState >= WebSocket.CLOSING) connect();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") reviveIfClosed();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", reviveIfClosed);
    window.addEventListener("focus", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", reviveIfClosed);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [groupId, connect]);

  return {
    connect,
    disconnect,
    sendMessage,
    ensureConnected,
    waitForFlush,
    connectionState,
  };
};
